/**
 * @file DropdownMenu 下拉菜单
 * @description 操作菜单；支持分割线、分组标题、信息行；fade；视口边界；列表 ScrollArea。
 * 默认点击打开；`triggerMode="hover"` 时悬停打开（适合快捷键说明等只读浮层）。
 *
 * @whenToUse 表格行操作、更多按钮；需要分组展示的只读说明浮层（hover）。
 * @whenNotToUse 表单枚举选择（用 Select）；单行悬停文案（用 Tooltip）；常驻导航（用 Menu）；右键操作（用 ContextMenu）。
 */

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'

/** 离开触发器后，允许移入面板的宽限时间（ms） */
const HOVER_CLOSE_DELAY = 120

/** 面板开关动画时长（ms），与 `duration-200` 一致 */
const DROPDOWN_ANIMATION_MS = 200

export type DropdownMenuItem = {
  key: string
  /**
   * item：可点击项；
   * divider：分割线（忽略 label）；
   * label：分组标题（不可点击）；
   * info：只读说明行（不可点击）
   */
  type?: 'item' | 'divider' | 'label' | 'info'
  icon?: ReactNode
  label?: ReactNode
  disabled?: boolean
  danger?: boolean
  onSelect?: () => void
}

export type DropdownMenuProps = {
  /** 触发器（通常是 Button） */
  trigger: ReactNode
  items: DropdownMenuItem[]
  align?: 'start' | 'end'
  /**
   * 打开方式。
   * - `click`：点击切换（默认）
   * - `hover`：悬停打开，移入面板可保持；触发器本身无业务点击
   */
  triggerMode?: 'click' | 'hover'
  /** 面板最小宽度（px）；图标触发器等窄按钮场景需加大 */
  minWidth?: number
  /** 面板样式类 */
  panelClassName?: string
}

/** 菜单项高度（h-8） */
const ITEM_HEIGHT = 32
/** 分组标题高度 */
const LABEL_HEIGHT = 24
/** 分割线高度（my-1 + h-px） */
const DIVIDER_HEIGHT = 9
/** 列表上下内边距（p-1） */
const PANEL_PAD_Y = 8

/**
 * 估算菜单内容高度（含分割线、分组标题与内边距）
 * @param items 菜单项
 */
function estimateMenuHeight(items: DropdownMenuItem[]): number {
  return items.reduce((sum, item) => {
    if (item.type === 'divider') return sum + DIVIDER_HEIGHT
    if (item.type === 'label') return sum + LABEL_HEIGHT
    return sum + ITEM_HEIGHT
  }, PANEL_PAD_Y)
}

export function DropdownMenu({
  trigger,
  items,
  align = 'start',
  triggerMode = 'click',
  minWidth = 160,
  panelClassName,
}: DropdownMenuProps) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)

  const clearCloseTimer = () => {
    if (closeTimerRef.current == null) return
    window.clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }

  const openMenu = () => {
    clearCloseTimer()
    setOpen(true)
  }

  const scheduleClose = () => {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false)
      closeTimerRef.current = null
    }, HOVER_CLOSE_DELAY)
  }

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const next = computeDropdownBox(rect, 4, minWidth)
    if (align === 'end') {
      next.left = Math.max(8, rect.right - next.width)
    }
    setBox(next)
  }

  useEffect(() => {
    if (open) {
      setVisible(false)
      setMounted(true)
      return
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), DROPDOWN_ANIMATION_MS)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!mounted || !open) return
    updateBox()
  }, [mounted, open, align, minWidth])

  /** 双 rAF：先绘制 opacity-0，再切到可见态，transition 才能生效 */
  useEffect(() => {
    if (!mounted || !open) return
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setVisible(true))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [mounted, open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    const onPointer = (event: MouseEvent) => {
      if (triggerMode === 'hover') return
      const t = event.target as Node
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, triggerMode, align, minWidth])

  useEffect(() => () => clearCloseTimer(), [])

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-flex"
        onClick={
          triggerMode === 'click'
            ? () => setOpen((v) => !v)
            : undefined
        }
        onMouseEnter={triggerMode === 'hover' ? openMenu : undefined}
        onMouseLeave={triggerMode === 'hover' ? scheduleClose : undefined}
      >
        {trigger}
      </div>
      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-sidebar/80 backdrop-blur-sm',
                'transition-[opacity,transform] duration-200 ease-out',
                visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
                panelClassName,
              )}
              style={{
                ...floatingPanelStyle(box),
                transformOrigin: box.placement === 'bottom' ? 'top center' : 'bottom center',
              }}
              onMouseEnter={triggerMode === 'hover' ? openMenu : undefined}
              onMouseLeave={triggerMode === 'hover' ? scheduleClose : undefined}
            >
              <ScrollArea
                className="w-full"
                style={{
                  height: Math.min(box.maxHeight, estimateMenuHeight(items)),
                }}
              >
                <div className="p-1">
                  {items.map((item) => {
                    if (item.type === 'divider') {
                      return (
                        <div
                          key={item.key}
                          role="separator"
                          className="my-1 h-px bg-border"
                        />
                      )
                    }

                    if (item.type === 'label') {
                      return (
                        <div
                          key={item.key}
                          className="flex h-6 items-center px-2 text-[10px] font-medium text-muted"
                        >
                          {item.label}
                        </div>
                      )
                    }

                    if (item.type === 'info') {
                      return (
                        <div
                          key={item.key}
                          role="presentation"
                          className="flex h-8 w-full items-center gap-1 rounded-md px-2 text-sm text-foreground"
                        >
                          {item.icon}
                          {item.label ? (
                            <span className="min-w-0 flex-1">{item.label}</span>
                          ) : null}
                        </div>
                      )
                    }

                    return (
                      <button
                        key={item.key}
                        type="button"
                        role="menuitem"
                        disabled={item.disabled}
                        className={cn(
                          'flex h-8 w-full items-center gap-1 rounded-md px-2 text-left text-sm transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50',
                          item.danger
                            ? 'text-danger hover:bg-danger/10 hover:text-danger'
                            : 'text-foreground hover:bg-accent/25 hover:text-primary',
                        )}
                        onClick={() => {
                          if (item.disabled) return
                          item.onSelect?.()
                          setOpen(false)
                        }}
                      >
                        {item.icon}
                        {item.label ? (
                          <span>{item.label}</span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              </ScrollArea>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
